import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { campaignsAPI, campaignProvisionsAPI, certificateAPI, quarterlyProvisionsAPI } from '../api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Euro, 
  Calendar,
  CheckCircle,
  Circle,
  Banknote,
  Loader2,
  Users,
  ArrowUpCircle,
  ArrowDownCircle,
  Calculator,
  Sparkles,
  Award,
  Mail,
  MapPin,
  Phone,
  Download,
  Share2,
  Save,
  Percent,
  Trash2,
  RefreshCw
} from 'lucide-react';
import { captureCertificateAsDataUrl, downloadCertificateAsPdf } from '../utils/certificateCapture';
import { WAHR_LOGO_BASE64, SIGNATURE_BASE64 } from '../utils/certificateImages';

const TrainerProvisionView = ({ filterCampaignId, filterQuarter, filterYear }) => {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [selectedYear, setSelectedYear] = useState(filterYear ? String(filterYear) : '2025');
  const [selectedCampaign, setSelectedCampaign] = useState(filterCampaignId || '');
  const [provision, setProvision] = useState(null);
  const [multipliers, setMultipliers] = useState({ multiplier_w12: 1.4, multiplier_w3: 1.0, mwst_rate: 0.19 });
  const [loading, setLoading] = useState(true);
  
  // Track if we should show quarterly view
  const [showQuarterlyView, setShowQuarterlyView] = useState(!!filterQuarter);
  const [activeQuarter, setActiveQuarter] = useState(filterQuarter || null);
  
  // Quarterly Neuwerber state
  const [quarterlyProvisions, setQuarterlyProvisions] = useState({});
  const [quarterlyMultipliers, setQuarterlyMultipliers] = useState({});
  const [loadingQuarterly, setLoadingQuarterly] = useState(false);
  
  // Certificate state
  const [certificateDialogOpen, setCertificateDialogOpen] = useState(false);
  const [selectedTrainee, setSelectedTrainee] = useState(null);
  const [certificateData, setCertificateData] = useState(null);
  const [certificateNotes, setCertificateNotes] = useState('');
  const [generatingCertificate, setGeneratingCertificate] = useState(false);
  const [savingCertificate, setSavingCertificate] = useState(false);
  const [isQuarterlyTrainee, setIsQuarterlyTrainee] = useState(false);
  const [selectedQuarterForCertificate, setSelectedQuarterForCertificate] = useState(null);
  const [selectedRank, setSelectedRank] = useState('');
  const [certDateFrom, setCertDateFrom] = useState('');
  const [certDateTo, setCertDateTo] = useState('');
  const certificateRef = useRef(null);
  
  // Rangsystem basierend auf Einheiten
  const RANKS = [
    { id: 'SS', title: 'Legende', points: 1000, color: '#FFD700', bgColor: '#FFF9E6' },
    { id: 'S', title: 'Elite', points: 800, color: '#C0C0C0', bgColor: '#F5F5F5' },
    { id: 'A', title: 'Profi', points: 600, color: '#CD7F32', bgColor: '#FDF5E6' },
    { id: 'B', title: 'Aufsteiger', points: 400, color: '#4b95d1', bgColor: '#E6F3FF' },
    { id: 'C', title: 'Starter', points: 200, color: '#6B7280', bgColor: '#F3F4F6' },
  ];
  
  const getRankData = (rankId) => RANKS.find(r => r.id === rankId);

  // Update state when filter props change
  useEffect(() => {
    if (filterCampaignId) {
      setSelectedCampaign(filterCampaignId);
      setShowQuarterlyView(false);
    }
    if (filterQuarter) {
      setShowQuarterlyView(true);
      setActiveQuarter(filterQuarter);
    }
    if (filterYear) {
      setSelectedYear(String(filterYear));
    }
  }, [filterCampaignId, filterQuarter, filterYear]);

  // Auto-refresh when component mounts or becomes visible
  useEffect(() => {
    loadCampaigns();
  }, [selectedYear]);

  useEffect(() => {
    if (selectedCampaign) {
      loadProvision();
    }
  }, [selectedCampaign]);

  // Refresh data when window gains focus (user comes back to tab)
  useEffect(() => {
    const handleFocus = () => {
      if (selectedCampaign) {
        loadProvision();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [selectedCampaign]);

  const loadCampaigns = async () => {
    try {
      const [data, myProvisions] = await Promise.all([
        campaignsAPI.getAll(parseInt(selectedYear)),
        campaignProvisionsAPI.getMineAll()
      ]);
      // Trainer can only pick KWs where trainees/provision exist for them
      const myIds = new Set(
        myProvisions
          .filter(p => (p.trainees || []).length > 0 || (p.total_provision_brutto || 0) > 0)
          .map(p => p.campaign_id)
      );
      const filtered = data.filter(c => myIds.has(c.id) || c.id === filterCampaignId);
      setCampaigns(filtered);
      // Only auto-select if no filterCampaignId was provided
      if (filtered.length > 0 && !selectedCampaign && !filterCampaignId) {
        const current = filtered.find(c => c.status === 'active') || filtered[0];
        setSelectedCampaign(current.id);
      }
    } catch (error) {
      console.error('Error loading campaigns:', error);
    }
  };

  const loadProvision = async () => {
    setLoading(true);
    try {
      const data = await campaignProvisionsAPI.getMine(selectedCampaign);
      setProvision(data);
      
      // Extract multipliers from provision data
      if (data) {
        setMultipliers({
          multiplier_w12: data.multiplier_w12 ?? 1.4,
          multiplier_w3: data.multiplier_w3 ?? 1.0,
          mwst_rate: data.mwst_rate ?? 0.19
        });
      }
    } catch (error) {
      console.error('Error loading provision:', error);
      setProvision(null);
    }
    setLoading(false);
  };

  // Load all quarterly provisions for the selected year
  const loadQuarterlyProvisions = async () => {
    if (!user?.id) return;
    
    setLoadingQuarterly(true);
    const newQuarterlyProvisions = {};
    const newQuarterlyMultipliers = {};
    
    try {
      // Load all 4 quarters using the trainer's own endpoint
      for (let quarter = 1; quarter <= 4; quarter++) {
        try {
          const data = await quarterlyProvisionsAPI.getMine(parseInt(selectedYear), quarter);
          if (data && data.trainees && data.trainees.length > 0) {
            newQuarterlyProvisions[quarter] = data;
            newQuarterlyMultipliers[quarter] = {
              multiplier: data.multiplier ?? 1.0,
              mwst_rate: data.mwst_rate ?? 0.19
            };
          }
        } catch (err) {
          // Quarter might not have data, that's okay - silently skip
        }
      }
      setQuarterlyProvisions(newQuarterlyProvisions);
      setQuarterlyMultipliers(newQuarterlyMultipliers);
    } catch (error) {
      console.error('Error loading quarterly provisions:', error);
    }
    setLoadingQuarterly(false);
  };

  // Load quarterly data when year changes
  useEffect(() => {
    if (user?.id) {
      loadQuarterlyProvisions();
    }
  }, [selectedYear, user?.id]);

  const deleteTrainee = async (traineeId) => {
    if (!window.confirm('Möchten Sie diesen Trainee wirklich löschen?')) return;
    
    try {
      await campaignProvisionsAPI.deleteMyTrainee(selectedCampaign, traineeId);
      await loadProvision();
    } catch (error) {
      console.error('Error deleting trainee:', error);
      alert(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  // Trainees grouped by week
  const trainees = provision?.trainees || [];
  const traineesW1 = trainees.filter(t => (t.units_week1 || 0) > 0 && (t.units_week2 || 0) === 0);
  const traineesW2 = trainees.filter(t => (t.units_week2 || 0) > 0);
  // W3 jetzt nur noch in Quartalsbereichen
  const traineesNoUnits = trainees.filter(t => 
    (t.units_week1 || 0) === 0 && 
    (t.units_week2 || 0) === 0
  );
  
  // Also include trainees with W1 and W2 units in both weeks
  const traineesW1Full = trainees.filter(t => (t.units_week1 || 0) > 0);
  const traineesW2Full = trainees.filter(t => (t.units_week2 || 0) > 0);

  const totalUnitsW1 = traineesW1Full.reduce((sum, t) => sum + (t.units_week1 || 0), 0);
  const totalUnitsW2 = traineesW2Full.reduce((sum, t) => sum + (t.units_week2 || 0), 0);

  // Certificate functions
  const formatCertDateDE = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const toISODateStr = (d) => {
    if (!d) return '';
    if (/^\d{4}-\d{2}-\d{2}/.test(String(d))) return String(d).split('T')[0];
    const m = String(d).match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
    return '';
  };

  const deriveCampaignDatesISO = () => {
    const campaign = campaigns.find(c => c.id === selectedCampaign);
    let from = campaign?.start_date || '';
    let to = campaign?.end_date || '';
    if ((!from || !to) && selectedCampaign) {
      const m = String(selectedCampaign).match(/^(\d{4})-kw-?(\d+)-(\d+)$/i);
      if (m) {
        const year = parseInt(m[1]);
        const mondayOf = (week) => {
          const simple = new Date(year, 0, 1 + (week - 1) * 7);
          const dow = simple.getDay();
          const mon = new Date(simple);
          if (dow <= 4) mon.setDate(simple.getDate() - simple.getDay() + 1);
          else mon.setDate(simple.getDate() + 8 - simple.getDay());
          return mon;
        };
        const s = mondayOf(parseInt(m[2]));
        const e = mondayOf(parseInt(m[3]));
        e.setDate(e.getDate() + 6);
        from = s.toISOString().split('T')[0];
        to = e.toISOString().split('T')[0];
      }
    }
    return { from: toISODateStr(from), to: toISODateStr(to) };
  };

  const openCertificateDialog = (trainee, isQuarterly = false, quarter = null) => {
    setSelectedTrainee(trainee);
    setIsQuarterlyTrainee(isQuarterly);
    setSelectedQuarterForCertificate(quarter);
    const derived = deriveCampaignDatesISO();
    if (trainee.certificate) {
      setCertificateData(trainee.certificate);
      setCertificateNotes(trainee.certificate.notes || '');
      setSelectedRank(trainee.certificate.rank || '');
      setCertDateFrom(toISODateStr(trainee.certificate.date_from) || derived.from);
      setCertDateTo(toISODateStr(trainee.certificate.date_to) || derived.to);
    } else {
      setCertificateData(null);
      setCertificateNotes('');
      setSelectedRank('');
      setCertDateFrom(derived.from);
      setCertDateTo(derived.to);
    }
    setCertificateDialogOpen(true);
  };

  const generateCertificate = async () => {
    if (!certificateNotes.trim() || !selectedTrainee) return;
    
    setGeneratingCertificate(true);
    try {
      const campaign = campaigns.find(c => c.id === selectedCampaign);
      const response = await certificateAPI.generate({
        name: selectedTrainee.name,
        date_from: formatCertDateDE(certDateFrom),
        date_to: formatCertDateDE(certDateTo),
        topic: campaign?.name || 'Verkaufstraining',
        notes: certificateNotes
      });
      
      setCertificateData({
        ...response,
        notes: certificateNotes,
        rank: selectedRank
      });
    } catch (error) {
      console.error('Error generating certificate:', error);
      alert('Fehler beim Generieren des Zertifikats');
    }
    setGeneratingCertificate(false);
  };

  const saveCertificate = async () => {
    if (!certificateData || !selectedTrainee) return;
    
    setSavingCertificate(true);
    try {
      await campaignProvisionsAPI.updateMyCertificate(
        selectedCampaign,
        selectedTrainee.id,
        {
          ...certificateData,
          date_from: formatCertDateDE(certDateFrom),
          date_to: formatCertDateDE(certDateTo),
          rank: selectedRank,
          generated_at: new Date().toISOString()
        }
      );
      
      await loadProvision();
      alert('Zertifikat gespeichert!');
    } catch (error) {
      console.error('Error saving certificate:', error);
      alert('Fehler beim Speichern');
    }
    setSavingCertificate(false);
  };

  const downloadCertificate = async () => {
    if (!certificateRef.current) return;
    
    try {
      const dataUrl = await captureCertificateAsDataUrl(certificateRef.current);
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      if (isMobile) {
        // Use Blob URL instead of document.write for security (XSS prevention)
        const htmlContent = `<!DOCTYPE html>
          <html>
            <head>
              <title>Zertifikat - ${selectedTrainee?.name || 'Download'}</title>
              <meta charset="utf-8">
              <meta name="viewport" content="width=device-width, initial-scale=1">
            </head>
            <body style="margin:0;padding:20px;text-align:center;background:#f5f5f5;">
              <p style="color:#666;margin-bottom:20px;">Lange auf das Bild drücken zum Speichern</p>
              <img src="${dataUrl}" style="max-width:100%;border:1px solid #ddd;border-radius:8px;" alt="Zertifikat"/>
            </body>
          </html>`;
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const blobUrl = URL.createObjectURL(blob);
        const newWindow = window.open(blobUrl, '_blank');
        if (!newWindow) {
          // Fallback: direct download
          const link = document.createElement('a');
          link.download = `Zertifikat_${selectedTrainee?.name?.replace(/\s+/g, '_')}.png`;
          link.href = dataUrl;
          link.click();
        }
        // Clean up blob URL after a delay
        setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      } else {
        const link = document.createElement('a');
        link.download = `Zertifikat_${selectedTrainee?.name?.replace(/\s+/g, '_')}.png`;
        link.href = dataUrl;
        link.click();
      }
    } catch (error) {
      console.error('Error downloading certificate:', error);
      alert('Fehler beim Download');
    }
  };

  const selectedCampaignData = campaigns.find(c => c.id === selectedCampaign);

  if (loading && !provision) {
    return (
      <Card className="shadow-lg">
        <CardContent className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#4b95d1]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="shadow-lg">
        <CardHeader className="bg-gradient-to-r from-[#1c2e52] to-[#3e4350] text-white rounded-t-lg">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <CardTitle className="flex items-center gap-2">
              <Calculator className="w-4 h-4 sm:w-5 sm:h-5 text-[#4b95d1]" />
              <span className="text-sm sm:text-base">Meine Provisionen</span>
            </CardTitle>
            <div className="flex gap-1 sm:gap-2 items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={loadProvision}
                disabled={loading}
                className="text-white hover:bg-white/20 h-8 w-8 p-0 sm:h-9 sm:w-9"
                title="Daten aktualisieren"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Select value={selectedYear} onValueChange={(val) => { setSelectedYear(val); setSelectedCampaign(''); }}>
                <SelectTrigger className="w-[70px] sm:w-[100px] bg-white/10 border-white/20 text-white text-xs sm:text-sm h-8 sm:h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2026">2026</SelectItem>
                </SelectContent>
              </Select>
              <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
                <SelectTrigger className="w-[100px] sm:w-[140px] bg-white/10 border-white/20 text-white text-xs sm:text-sm h-8 sm:h-9">
                  <SelectValue placeholder="Kampagne" />
                </SelectTrigger>
                <SelectContent>
                  {campaigns.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-3 sm:p-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-[#4b95d1]" />
            </div>
          ) : !provision ? (
            <div className="text-center py-8 text-[#3e4350] text-sm">
              Keine Provisionsdaten für diese Kampagne vorhanden.
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {/* Summary Stats - Responsive Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                <div className="p-2 sm:p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-[10px] sm:text-xs text-[#3e4350]">Einheiten</p>
                  <p className="text-base sm:text-lg font-bold text-blue-700">{formatNumber(provision.total_units || 0)}</p>
                </div>
                <div className="p-2 sm:p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-[10px] sm:text-xs text-[#3e4350]">Netto</p>
                  <p className="text-base sm:text-lg font-bold text-slate-700">{formatCurrency(provision.total_provision_netto || 0)}</p>
                </div>
                <div className="p-2 sm:p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-[10px] sm:text-xs text-[#3e4350]">Brutto</p>
                  <p className="text-base sm:text-lg font-bold text-green-700">{formatCurrency(provision.total_provision_brutto || 0)}</p>
                </div>
                <div className="p-2 sm:p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-[10px] sm:text-xs text-[#3e4350]">Vorschüsse</p>
                  <p className="text-base sm:text-lg font-bold text-amber-600">-{formatCurrency(provision.total_advances || 0)}</p>
                </div>
                <div className="p-2 sm:p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-[10px] sm:text-xs text-[#3e4350]">Ausgezahlt</p>
                  <p className="text-base sm:text-lg font-bold text-emerald-600">-{formatCurrency(provision.total_payouts || 0)}</p>
                </div>
                <div className="p-2 sm:p-3 bg-[#1c2e52] text-white rounded-lg col-span-2 sm:col-span-1">
                  <p className="text-[10px] sm:text-xs text-[#e6d7bf]">Offen</p>
                  <p className="text-base sm:text-lg font-bold text-[#4b95d1]">{formatCurrency(provision.remaining_amount || 0)}</p>
                </div>
              </div>
              
              {/* Transparenz: Berechnungsfaktoren */}
              <div className="flex items-center gap-2 flex-wrap text-[11px] sm:text-xs text-[#3e4350] bg-blue-50/60 border border-blue-100 rounded-lg px-3 py-1.5" data-testid="trainer-factor-info">
                <Percent className="w-3.5 h-3.5 text-[#4b95d1]" />
                <span>
                  Berechnung: Einheiten × Faktor <b>×{multipliers.multiplier_w12}</b> = Netto,
                  + <b>{(multipliers.mwst_rate * 100).toFixed(0)} % USt</b> = Brutto
                </span>
                {provision.shared_from && (
                  <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-[10px]">Geteilte Kampagne</Badge>
                )}
              </div>

              {/* Vorschüsse & Auszahlungen (Historie, wie beim Admin - nur Ansicht) */}
              {(((provision.advances || []).length + (provision.payouts || []).length) > 0
                || (provision.trainees || []).some(t => t.fee_received)) && (
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 space-y-1.5" data-testid="trainer-payout-history">
                  <p className="text-xs font-semibold text-[#1c2e52] flex items-center gap-1.5">
                    <Banknote className="w-3.5 h-3.5 text-[#4b95d1]" />
                    Vorschüsse & Auszahlungen
                  </p>
                  {(provision.advances || []).map((a, i) => (
                    <div key={a.id || `a-${i}`} className="flex justify-between items-center text-xs">
                      <span className="text-gray-600">
                        Vorschuss · {a.date ? new Date(a.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''}{a.note ? ` · ${a.note}` : ''}
                      </span>
                      <span className="font-semibold text-amber-600">{formatCurrency(a.amount)}</span>
                    </div>
                  ))}
                  {(provision.payouts || []).map((po, i) => (
                    <div key={po.id || `p-${i}`} className="flex justify-between items-center text-xs">
                      <span className="text-gray-600">
                        Auszahlung · {po.date ? new Date(po.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''}{po.note ? ` · ${po.note}` : ''}
                      </span>
                      <span className="font-semibold text-emerald-600">{formatCurrency(po.amount)}</span>
                    </div>
                  ))}
                  {(provision.trainees || []).filter(t => t.fee_received).map((t, i) => (
                    <div key={t.id || `f-${i}`} className="flex justify-between items-center text-xs">
                      <span className="text-gray-600">Ausgezahlt · {t.name}</span>
                      <span className="font-semibold text-emerald-600">{formatCurrency(t.provision_brutto || 0)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Pre-registered Trainees (no units yet) */}
              {traineesNoUnits.length > 0 && (
                <Card className="border-gray-200 bg-gray-50/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 text-gray-600">
                      <Users className="w-4 h-4" />
                      Vorbereitung ({traineesNoUnits.length} Trainees)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1">
                    {traineesNoUnits.map(trainee => (
                      <div key={trainee.id} className="flex items-center justify-between p-2 bg-white rounded border">
                        <div className="flex items-center gap-2">
                          <span className="text-sm">{trainee.name}</span>
                          {trainee.email && <Mail className="w-3 h-3 text-gray-400" />}
                          {trainee.phone && <Phone className="w-3 h-3 text-gray-400" />}
                          {trainee.certificate && <Award className="w-3 h-3 text-amber-500" />}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openCertificateDialog(trainee)}
                            className={`h-7 w-7 p-0 ${trainee.certificate ? 'text-amber-500' : 'text-gray-500'}`}
                          >
                            <Award className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteTrainee(trainee.id)}
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Kampagnen-Einheiten Block (kombiniert W1+W2) */}
              {(traineesW1Full.length > 0 || traineesW2Full.length > 0) && (
                <Card className="border-2 border-blue-300 bg-gradient-to-br from-blue-50 to-indigo-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 text-blue-700">
                      <Users className="w-4 h-4" />
                      Kampagnen-Einheiten
                      <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                        {formatNumber(totalUnitsW1 + totalUnitsW2)} E gesamt
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 max-h-[300px] overflow-y-auto">
                    {trainees.filter(t => (t.units_week1 || 0) > 0 || (t.units_week2 || 0) > 0).map(trainee => {
                      const traineeUnits = (trainee.units_week1 || 0) + (trainee.units_week2 || 0);
                      return (
                        <div key={trainee.id} className={`flex items-center gap-3 p-3 rounded-lg border ${
                          trainee.fee_received ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200'
                        }`}>
                          <Checkbox checked={trainee.fee_received} disabled className="border-green-500 h-5 w-5" />
                          <span className={`flex-1 font-medium ${trainee.fee_received ? 'line-through text-gray-400' : ''}`}>
                            {trainee.name}
                            {trainee.certificate_from_other_campaign && (
                              <span className="ml-1 text-xs text-amber-600">(Zertifikat aus anderer KW)</span>
                            )}
                          </span>
                          <Badge className="bg-blue-100 text-blue-700 px-3">{formatNumber(traineeUnits)} E</Badge>
                          <span className="font-bold text-blue-600 min-w-[80px] text-right">
                            {formatCurrency(traineeUnits * multipliers.multiplier_w12 * (1 + multipliers.mwst_rate))}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openCertificateDialog(trainee)}
                            className={`h-8 w-8 p-0 rounded-lg ${trainee.certificate ? 'bg-amber-100 text-amber-500' : 'text-gray-400 hover:bg-blue-100'}`}
                            title={trainee.certificate ? "Zertifikat anzeigen" : "Zertifikat erstellen"}
                          >
                            <Award className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}

              {/* Quarterly Neuwerber Sections - Shows W3 data from all quarters */}
              {Object.keys(quarterlyProvisions).length > 0 && (
                <div className="space-y-4 pt-4 border-t-4 border-purple-400">
                  <div className="flex items-center gap-3">
                    <div className="bg-purple-500 text-white p-2 rounded-lg">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-purple-700">
                        Quartalsabrechnung {selectedYear} - Neuwerber (Woche 3)
                      </h2>
                      <p className="text-sm text-purple-500">
                        Separate Übersicht aller Neuwerber pro Quartal
                      </p>
                    </div>
                    {loadingQuarterly && <Loader2 className="w-4 h-4 animate-spin text-purple-500" />}
                  </div>

                  {[1, 2, 3, 4].map(quarter => {
                    const qProvision = quarterlyProvisions[quarter];
                    if (!qProvision || !qProvision.trainees || qProvision.trainees.length === 0) return null;
                    
                    const qMultipliers = quarterlyMultipliers[quarter] || { multiplier: 1.0, mwst_rate: 0.19 };
                    const traineesWithW3 = qProvision.trainees.filter(t => (t.units || 0) > 0);
                    const traineesWithoutW3 = qProvision.trainees.filter(t => (t.units || 0) === 0);
                    const totalQUnits = qProvision.trainees.reduce((sum, t) => sum + (t.units || 0), 0);
                    const totalQBrutto = qProvision.total_provision_brutto || (totalQUnits * qMultipliers.multiplier * (1 + qMultipliers.mwst_rate));
                    const totalQNetto = qProvision.total_provision_netto || (totalQUnits * qMultipliers.multiplier);
                    const qPaid = typeof qProvision.total_payouts === 'number'
                      ? qProvision.total_payouts
                      : qProvision.trainees.reduce((s, t) => s + (t.fee_received ? (t.provision_brutto || 0) : 0), 0);
                    const qOpen = typeof qProvision.remaining_amount === 'number' ? qProvision.remaining_amount : totalQBrutto - qPaid;

                    return (
                      <Card key={quarter} className="border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-violet-50">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm flex items-center justify-between text-purple-700">
                            <div className="flex items-center gap-2">
                              <Sparkles className="w-4 h-4" />
                              Q{quarter}/{selectedYear} Neuwerber ({qProvision.trainees.length} Trainees)
                              <Badge variant="outline" className="text-xs border-purple-400 text-purple-600 bg-purple-100">
                                ×{qMultipliers.multiplier} ×{(1 + qMultipliers.mwst_rate).toFixed(2).replace('.', ',')}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap justify-end">
                              <span className="text-xs text-purple-500">
                                Netto: {formatCurrency(totalQNetto)}
                              </span>
                              <span className="text-sm font-bold text-purple-700">
                                {formatCurrency(totalQBrutto)}
                              </span>
                              {qOpen > 0 ? (
                                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">
                                  Offen: {formatCurrency(qOpen)}
                                </Badge>
                              ) : totalQBrutto > 0 ? (
                                <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-[10px]">
                                  Ausgezahlt
                                </Badge>
                              ) : null}
                            </div>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          {/* W3 Status Summary */}
                          <div className="flex items-center gap-4 bg-gray-50 rounded-lg p-2 text-xs">
                            <div className="flex items-center gap-1">
                              <CheckCircle className="w-3 h-3 text-green-500" />
                              <span className="text-green-700">{traineesWithW3.length} mit W3</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Circle className="w-3 h-3 text-gray-400" />
                              <span className="text-gray-500">{traineesWithoutW3.length} noch keine W3</span>
                            </div>
                          </div>
                          
                          {/* Trainees List */}
                          <div className="space-y-1 max-h-[200px] overflow-y-auto">
                            {qProvision.trainees.map(trainee => {
                              const hasW3 = (trainee.units || 0) > 0;
                              return (
                                <div key={trainee.id} className={`flex items-center gap-2 p-2 rounded border ${
                                  trainee.fee_received 
                                    ? 'bg-green-50 border-green-300' 
                                    : hasW3 
                                      ? 'bg-white border-purple-200' 
                                      : 'bg-gray-50 border-gray-200'
                                }`}>
                                  <Checkbox checked={trainee.fee_received} disabled className="border-purple-400" />
                                  {/* W3 Status Icon */}
                                  <div className="flex-shrink-0">
                                    {hasW3 ? (
                                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100 text-green-600" title="Hat W3 gemacht">
                                        <CheckCircle className="w-3 h-3" />
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 text-gray-400" title="Noch keine W3">
                                        <Circle className="w-3 h-3" />
                                      </span>
                                    )}
                                  </div>
                                  <span className={`flex-1 text-sm truncate ${
                                    trainee.fee_received 
                                      ? 'line-through text-gray-400' 
                                      : hasW3 ? '' : 'text-gray-500'
                                  }`}>
                                    {trainee.name}
                                  </span>
                                  {trainee.from_campaign && (
                                    <Badge variant="outline" className="text-[10px] border-gray-300 text-gray-500 px-1">
                                      aus Kampagne
                                    </Badge>
                                  )}
                                  <Badge variant="outline" className={`text-xs ${hasW3 ? 'border-purple-300' : 'border-gray-300 text-gray-500'}`}>
                                    {formatNumber(trainee.units || 0)} E
                                  </Badge>
                                  <span className={`text-sm font-medium ${hasW3 ? 'text-purple-700' : 'text-gray-400'}`}>
                                    {formatCurrency(trainee.provision_brutto || ((trainee.units || 0) * qMultipliers.multiplier * (1 + qMultipliers.mwst_rate)))}
                                  </span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openCertificateDialog(trainee, true, quarter)}
                                    className={`h-6 w-6 p-0 ${trainee.certificate ? 'text-amber-500' : 'text-gray-400'}`}
                                    title={trainee.certificate ? "Zertifikat anzeigen" : "Zertifikat erstellen"}
                                  >
                                    <Award className="w-3 h-3" />
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Advances & Payouts */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Advances */}
                {(provision.advances?.length > 0) && (
                  <Card className="border-amber-200 bg-amber-50/30">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2 text-amber-700">
                        <ArrowUpCircle className="w-4 h-4" />
                        Vorschüsse ({provision.advances.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1">
                      {provision.advances.map(adv => (
                        <div key={adv.id} className="flex justify-between items-center p-2 bg-white rounded border border-amber-200">
                          <div className="text-sm">
                            <span>{adv.date}</span>
                            {adv.note && <span className="text-gray-500 ml-2">- {adv.note}</span>}
                          </div>
                          <span className="font-medium text-amber-600">-{formatCurrency(adv.amount)}</span>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Payouts */}
                {(provision.payouts?.length > 0) && (
                  <Card className="border-green-200 bg-green-50/30">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2 text-green-700">
                        <ArrowDownCircle className="w-4 h-4" />
                        Auszahlungen ({provision.payouts.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1">
                      {provision.payouts.map(pay => (
                        <div key={pay.id} className="flex justify-between items-center p-2 bg-white rounded border border-green-200">
                          <div className="text-sm">
                            <span>{pay.date}</span>
                            {pay.note && <span className="text-gray-500 ml-2">- {pay.note}</span>}
                          </div>
                          <span className="font-medium text-green-600">{formatCurrency(pay.amount)}</span>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Certificate Dialog */}
      <Dialog open={certificateDialogOpen} onOpenChange={setCertificateDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" />
              Zertifikat für {selectedTrainee?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Input */}
            <div className="space-y-4">
              {/* Zeitraum (aus Kampagne vorbefüllt, bei Bedarf anpassbar) */}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Von</Label>
                  <Input type="date" value={certDateFrom} onChange={(e) => setCertDateFrom(e.target.value)} className="h-9" data-testid="trainer-cert-date-from" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Bis</Label>
                  <Input type="date" value={certDateTo} onChange={(e) => setCertDateTo(e.target.value)} className="h-9" data-testid="trainer-cert-date-to" />
                </div>
              </div>
              {/* Notes Input */}
              <div className="space-y-2">
                <Label>Stichpunkte für das Zertifikat</Label>
                <Textarea
                  placeholder="z.B. schnelle Auffassungsgabe, sehr engagiert, gute Teamarbeit..."
                  value={certificateNotes}
                  onChange={(e) => setCertificateNotes(e.target.value)}
                  className="min-h-[100px]"
                />
              </div>
              
              {/* Rang Auswahl */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-amber-500" />
                  Erreichter Rang
                </Label>
                <Select value={selectedRank} onValueChange={setSelectedRank}>
                  <SelectTrigger>
                    <SelectValue placeholder="Rang auswählen (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {RANKS.map(rank => (
                      <SelectItem key={rank.id} value={rank.id}>
                        <div className="flex items-center gap-2">
                          <span className="font-bold" style={{ color: rank.color }}>Rang {rank.id}</span>
                          <span className="text-gray-600">- {rank.title}</span>
                          <span className="text-xs text-gray-400">({rank.points} Pkt)</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button
                onClick={generateCertificate}
                disabled={!certificateNotes.trim() || generatingCertificate}
                className="w-full bg-[#4b95d1] hover:bg-[#1c2e52]"
              >
                {generatingCertificate ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Zertifikat generieren
              </Button>
            </div>
            
            {/* Right Column - Preview */}
            <div className="flex justify-center">
              {certificateData ? (
                <div 
                  ref={certificateRef}
                  className="bg-white rounded-lg border-[6px] border-double border-[#1c2e52] relative"
                  style={{ 
                    backgroundImage: `url(${WAHR_LOGO_BASE64})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    backgroundSize: '35%',
                    backgroundBlendMode: 'overlay',
                    width: '480px',
                    minWidth: '480px',
                    minHeight: '679px',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '24px 28px'
                  }}
                >
                  <div className="absolute inset-0 bg-white/90 rounded-lg" />
                  <div className="relative z-10 flex flex-col flex-1">
                    {/* Rang Badge - oben rechts */}
                    {selectedRank && getRankData(selectedRank) && (
                      <div style={{ 
                        position: 'absolute', 
                        top: '-4px', 
                        right: '-4px', 
                        zIndex: 2
                      }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor: getRankData(selectedRank).bgColor,
                          border: `2px solid ${getRankData(selectedRank).color}`,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          textAlign: 'center',
                          overflow: 'hidden',
                          boxSizing: 'border-box'
                        }}>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: getRankData(selectedRank).color, lineHeight: '24px', marginTop: '6px' }}>{selectedRank}</div>
                          <div style={{ fontSize: '6px', color: getRankData(selectedRank).color, fontWeight: '600', lineHeight: '8px' }}>{getRankData(selectedRank).title}</div>
                        </div>
                      </div>
                    )}
                    
                    {/* Header - kompakt */}
                    <div className="flex justify-center mb-3">
                      <img src={WAHR_LOGO_BASE64} alt="WAHR Logo" className="h-10 object-contain" />
                    </div>
                    <div className="text-center mb-4">
                      <h1 className="text-xl font-bold text-[#1c2e52] mb-1">SCHULUNGSZERTIFIKAT</h1>
                      <p className="text-xs text-[#3e4350]">WAHR - Privatinstitut für Dialog & Markenkompetenz</p>
                    </div>

                    {/* Name - kompakt */}
                    <div className="text-center mb-4">
                      <p className="text-sm text-[#3e4350]">Hiermit bestätigen wir, dass</p>
                      <p className="text-lg font-bold text-[#1c2e52] my-2">{selectedTrainee?.name}</p>
                      <p className="text-xs text-[#3e4350]">
                        erfolgreich an der Schulung{certDateFrom && certDateTo ? ` vom ${formatCertDateDE(certDateFrom)} bis ${formatCertDateDE(certDateTo)}` : ''} teilgenommen hat.
                      </p>
                      {selectedRank && getRankData(selectedRank) && (
                        <span style={{ 
                          fontSize: '10px', 
                          fontWeight: 'bold',
                          color: getRankData(selectedRank).color, 
                          marginTop: '6px',
                          padding: '2px 10px',
                          backgroundColor: getRankData(selectedRank).bgColor,
                          borderRadius: '3px',
                          display: 'inline-block'
                        }}>
                          Rang {selectedRank} – {getRankData(selectedRank).title}
                        </span>
                      )}
                    </div>

                    {/* Content Sections - 2 Spalten für Kompaktheit */}
                    <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                      <div className="bg-[#e6d7bf]/20 p-2 rounded">
                        <h3 className="font-bold text-[#1c2e52] text-xs mb-1">Allgemeiner Eindruck</h3>
                        <p className="text-[#3e4350] text-[10px] leading-tight">{certificateData.allgemeiner_eindruck}</p>
                      </div>
                      <div className="bg-[#4b95d1]/10 p-2 rounded">
                        <h3 className="font-bold text-[#1c2e52] text-xs mb-1">Teamverhalten</h3>
                        <p className="text-[#3e4350] text-[10px] leading-tight">{certificateData.teamverhalten}</p>
                      </div>
                      <div className="bg-[#4b95d1]/10 p-2 rounded">
                        <h3 className="font-bold text-[#1c2e52] text-xs mb-1">Arbeitsweise</h3>
                        <p className="text-[#3e4350] text-[10px] leading-tight">{certificateData.arbeitsweise}</p>
                      </div>
                      <div className="bg-[#e6d7bf]/20 p-2 rounded">
                        <h3 className="font-bold text-[#1c2e52] text-xs mb-1">Leistungsentwicklung</h3>
                        <p className="text-[#3e4350] text-[10px] leading-tight">{certificateData.leistungsentwicklung}</p>
                      </div>
                    </div>
                    
                    {/* Fazit - volle Breite */}
                    <div className="bg-[#1c2e52]/10 p-2 rounded mb-3">
                      <h3 className="font-bold text-[#1c2e52] text-xs mb-1">Fazit</h3>
                      <p className="text-[#3e4350] text-[10px] leading-tight">{certificateData.fazit}</p>
                    </div>

                    {/* Signature - kompakt */}
                    <div className="flex justify-between items-end pt-2 border-t border-[#1c2e52] mt-auto">
                      <div className="text-center">
                        <p className="text-[10px] text-[#3e4350]">Monheim am Rhein, {new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</p>
                        <p className="text-[8px] text-[#3e4350]">Ort, Datum</p>
                      </div>
                      <div className="text-center">
                        <img src={SIGNATURE_BASE64} alt="Unterschrift" className="h-8 mx-auto" />
                        <div className="border-t border-[#1c2e52] pt-0.5 px-4">
                          <p className="text-[8px] text-[#3e4350]">Schulungsleitung</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 border-2 border-dashed rounded-lg p-8 text-center h-full flex flex-col items-center justify-center">
                  <Award className="w-12 h-12 text-gray-300 mb-3" />
                  <p className="text-gray-500">Stichpunkte eingeben und &quot;Generieren&quot; klicken</p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            {certificateData && (
              <>
                <Button variant="outline" onClick={downloadCertificate} data-testid="trainer-cert-download-png">
                  <Download className="w-4 h-4 mr-2" />
                  PNG
                </Button>
                <Button variant="outline" onClick={() => downloadCertificateAsPdf(certificateRef.current, `Zertifikat_${selectedTrainee?.name?.replace(/\s+/g, '_')}.pdf`)} data-testid="trainer-cert-download-pdf">
                  <Download className="w-4 h-4 mr-2" />
                  PDF (A4)
                </Button>
                <Button onClick={saveCertificate} disabled={savingCertificate} className="bg-[#4b95d1]">
                  {savingCertificate ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Speichern
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setCertificateDialogOpen(false)}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TrainerProvisionView;
